import { Box, alpha, styled, useTheme } from '@mui/material';
import { type Theme } from '@mui/material/styles';
import {
  DataGrid,
  gridClasses,
  type GridCellParams,
  type GridColumns,
  type GridEventListener,
  type MuiEvent,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import { Fragment, useMemo, type ChangeEvent } from 'react';
import { colorTheme } from '../../theme';
import CustomTableActionButton from './CustomTableActionButton';
import CustomTableFooter from './CustomTableFooter';
import CustomTableHeader from './CustomTableHeader';
import { type PageType } from 'store/component/pagination';
import { commonConstantTexts } from 'constants/constantText';

export type GridRowData = Record<string, unknown>;
interface employer {
  employee?: GridRowData[];
}
interface CustomDataGridProps {
  flag?: boolean;
  title?: string;
  subtitle?: string;
  columns: GridColumns;
  rows?: GridRowData[];
  // pageSize?: number;
  isEnableActionBtn?: boolean;
  addButtonTitle?: string;
  addButtonTitle2?: string;
  editButtonTitle?: string;
  isShowExport?: boolean;
  searchEnabled?: boolean;
  currentPage: PageType;
  onAddUserClick?: () => void;
  onUploadButtonClick?: (e: ChangeEvent<HTMLInputElement>) => void;
  onEditClick?: (row: GridRowData) => void;
  onDeleteClick?: (row: GridRowData) => void;
  onAddButtonClick?: () => void;
  onAddButtonClick2?: () => void;
  onUpdateButtonClick?: () => void;
  onExportClick?: () => void;
  handleSearch?: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
  actionBtnText1?: string;
  pageCount?: number;
  actionBtnText2?: string;
  actionBtnText3?: string;
  ActionBtnIcon1?: React.ElementType;
  ActionBtnIcon2?: React.ElementType;
  ActionBtnIcon3?: React.ElementType;
  tableHeight?: string;

  hideActionsCondition?: (row: GridRowData) => boolean;
  hideActionBtnIcon1?: (row: GridRowData) => boolean;
  hideActionBtnIcon2?: (row: GridRowData) => boolean;
  hideActionBtnIcon3?: (row: GridRowData) => boolean;

  onActionBtnClick1?: (
    params: GridCellParams,
    event: MuiEvent<React.MouseEvent<HTMLElement>>,
  ) => void;
  onActionBtnClick2?: (
    params: GridCellParams & employer,
    event: MuiEvent<React.MouseEvent<HTMLElement>>,
  ) => void;
  onActionBtnClick3?: (params: GridCellParams) => void;
  getRowId: (row: GridRowData) => string;
  getRowClassName: (params: { indexRelativeToCurrentPage: number }) => string;
  onRowClick?: GridEventListener<'rowClick'>;
  sortModel: () => void;
  onSortModelChange: () => void;
  sortingMode: string;
  hideFooter?: boolean;
  searchPlaceholder?: string;
  rightContent?: React.ReactNode;
}

const CustomDataGrid = (props: CustomDataGridProps): JSX.Element => {
  const {
    title,
    subtitle,
    columns,
    rows,
    flag,
    pageCount,
    currentPage,
    addButtonTitle,
    addButtonTitle2,
    editButtonTitle,
    isShowExport,
    searchEnabled,
    onAddButtonClick,
    onAddButtonClick2,
    onUpdateButtonClick,
    onUploadButtonClick,
    onExportClick,
    handleSearch,
    actionBtnText1,
    actionBtnText2,
    actionBtnText3,
    ActionBtnIcon1,
    ActionBtnIcon2,
    ActionBtnIcon3,
    onActionBtnClick1,
    onActionBtnClick2,
    onActionBtnClick3,
    hideActionsCondition,
    getRowId,
    getRowClassName,
    onRowClick,
    sortModel,
    onSortModelChange,
    sortingMode,
    tableHeight,
    hideFooter,
    searchPlaceholder,
    hideActionBtnIcon1,
    hideActionBtnIcon2,
    hideActionBtnIcon3,
    rightContent,
  } = props;
  const currentTheme = useTheme();
  const colors = colorTheme(currentTheme.palette.mode);
  const ODD_OPACITY = 0.2;

  const StripedDataGrid = styled(DataGrid)(({ theme }: { theme: Theme }) => ({
    fontFamily: ['Inter', 'Source Sans Pro', 'sans-serif'].join(','),
    WebkitFontSmoothing: 'auto',
    letterSpacing: 'normal',
    [`& .${gridClasses.row}.even`]: {
      backgroundColor: colors.gray[900],
      '&:hover, &.Mui-hovered': {
        backgroundColor: alpha('#78B894', ODD_OPACITY),
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
      '&.Mui-selected': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY + theme.palette.action.selectedOpacity,
        ),
        '&:hover, &.Mui-hovered': {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY +
              theme.palette.action.selectedOpacity +
              theme.palette.action.hoverOpacity,
          ),
          '@media (hover: none)': {
            backgroundColor: alpha(
              theme.palette.primary.main,
              ODD_OPACITY + theme.palette.action.selectedOpacity,
            ),
          },
        },
      },
    },
    height: tableHeight ?? '75vh',
    backgroundColor: colors.primaryBg[900],
    borderRadius: 12,
    paddingBottom: 20,
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 10,
    '& .MuiDataGrid-root': {
      border: 'none',
    },
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
      wordBreak: 'break-word', // Ensure long words break and don't overflow
      whiteSpace: 'normal', // Allow text to wrap
      lineHeight: '3', // Adjust line height for better readability
    },
    '& .MuiDataGrid-columnHeader .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    '& .MuiDataGrid-columnHeader': {
      backgroundColor: colors.primaryBg[900],
      borderBottom: 'none',
    },
    '& .MuiDataGrid-virtualScroller': {
      backgroundColor: colors.primaryBg[900],
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  }));

  const ActionButton: React.FC<GridCellParams> = ({
    row,
  }: {
    row: GridRowData;
  }) => {
    return (
      <CustomTableActionButton
        row={row}
        actionBtnText1={actionBtnText1}
        actionBtnText2={actionBtnText2}
        actionBtnText3={actionBtnText3}
        ActionBtnIcon1={!hideActionBtnIcon1?.(row) ? ActionBtnIcon1 : undefined}
        ActionBtnIcon2={!hideActionBtnIcon2?.(row) ? ActionBtnIcon2 : undefined}
        ActionBtnIcon3={!hideActionBtnIcon3?.(row) ? ActionBtnIcon3 : undefined}
        onActionBtnClick1={onActionBtnClick1}
        onActionBtnClick2={onActionBtnClick2}
        onActionBtnClick3={onActionBtnClick3}
        disableCondition={
          !!(row.status && row.status === commonConstantTexts.success)
        }
      />
    );
  };

  const updatedColumns = useMemo(() => {
    const columnsCopy = [...columns];
    if (actionBtnText1 ?? actionBtnText2 ?? actionBtnText3) {
      columnsCopy.push({
        field: 'action',
        headerName: 'Actions',
        headerAlign: 'center',
        sortable: false,
        width: 150,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        renderCell: (props: GridRenderCellParams<any, any, any>) => {
          if (hideActionsCondition?.(props.row)) return null;

          return <ActionButton {...props} />;
        },
      });
    }
    return columnsCopy;
  }, [actionBtnText1, actionBtnText2, actionBtnText3, columns]);

  return (
    <Fragment>
      <Box>
        <CustomTableHeader
          flag={flag}
          title={title}
          subtitle={subtitle ?? ''}
          searchEnabled={searchEnabled}
          handleSearch={handleSearch}
          addButtonTitle={addButtonTitle}
          addButtonTitle2={addButtonTitle2}
          editButtonTitle={editButtonTitle}
          onAddButtonClick={onAddButtonClick}
          onAddButtonClick2={onAddButtonClick2}
          onUpdateButtonClick={onUpdateButtonClick}
          onUploadButtonClick={onUploadButtonClick}
          isShowExport={isShowExport}
          onExportClick={onExportClick}
          searchPlaceholder={searchPlaceholder}
          rightContent={rightContent}
        />
        <StripedDataGrid
          sx={{
            boxShadow: 2,
            border: 2,
            borderColor: colors.gray[800],
            '& .MuiDataGrid-cell:hover': {
              color: 'primary.main',
            },
            '& .MuiDataGrid-row': {
              color: colors.gray[800],
              borderTop: 1,
            },
          }}
          columns={
            actionBtnText1 ?? actionBtnText2 ?? actionBtnText3
              ? updatedColumns
              : columns
          }
          rows={rows}
          onRowClick={onRowClick}
          getRowId={getRowId}
          getRowClassName={getRowClassName}
          sortModel={sortModel}
          sortingMode={sortingMode}
          onSortModelChange={onSortModelChange}
          sortingOrder={['desc', 'asc']}
          components={{
            Footer: () =>
              currentPage && (
                <CustomTableFooter
                  currentPage={currentPage}
                  dataCount={pageCount}
                />
              ),
          }}
          hideFooter={hideFooter ?? false}
        />
      </Box>
    </Fragment>
  );
};

export default CustomDataGrid;
